//src/production/components/AddEditProductWiseMachine.jsx :-
import React, { useState, useEffect } from "react";
import { Box, Button, TextField, Select, MenuItem, FormControl, InputLabel, Typography, Grid } from "@mui/material";
import { grey } from "@mui/material/colors";
import axios from "axios";
import env from "react-dotenv";

const AddEditProductWiseMachine = ({ onClose, onSave, editData, onExpandDrawer }) => {
    const [showNewProductForm, setShowNewProductForm] = useState(false);
    const [showNewRawMaterialForm, setShowNewRawMaterialForm] = useState(false);
    const [showNewSectionForm, setShowNewSectionForm] = useState(false);
    const [showNewProcessForm, setShowNewProcessForm] = useState(false);
    const [showNewMachineForm, setShowNewMachineForm] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const [formData, setFormData] = useState({
        product_id: "",
        RM_id: "",
        section_id: "",
        process_id: "",
        machine_id: "",
        product_name: "",
        RM_name: "",
        section_name: "",
        process_name: "",
        machine_name: "",
        quantity: "",
        elect_unit: "",
        elect_cost: "",
        manpower: "",
        manpower_cost: "",
        manpower_cost_per_unit: "",
        elect_cost_per_unit: "",
        cost_unit: "",
        // New fields for the new product form
        new_product_name: "",
        full_name: "",
        // unit_cost: "",
        descrip: "",
        // New fields for the new raw material form
        new_RM_product_code: "",
        new_RM_product_name: "",
        new_RM_quantity: "",
        new_RM_daily_consumption: "",
        new_RM_backup_time: "",
        new_RM_base_uom: "",
        // New fields for the new section form
        new_section_name: "",
        new_section_remark: "",
        // New fields for the new process form
        new_process_name: "",
        new_process_remark: "",
        // New fields for the new machine form
        new_machine_no: "",
        new_machine_name: "",
        new_machine_start_date: "",
        new_machine_end_date: ""
    });

    const [options, setOptions] = useState({
        products: [],
        rawMaterials: [],
        sections: [],
        processes: [],
        // machines: []
    });

    const [filteredMachines, setFilteredMachines] = useState([]);

    useEffect(() => {
        fetchDropdownData();
        if (editData) {
            setFormData(editData);
            fetchMachinesByDepartment(editData.section_id); // Fetch machines for the selected department
        }
    }, [editData]);

    const fetchDropdownData = async () => {
        try {
            const [products, rawMaterials, sections, processes, machines] = await Promise.all([
                axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/main-product`),
                axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/products`),
                axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/department`),
                axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/proccess`)
            ]);
            setOptions({
                products: products.data,
                rawMaterials: rawMaterials.data,
                sections: sections.data,
                processes: processes.data,
                // machines: [] // Initialize machines as empty
            });
        } catch (error) {
            console.error("Error fetching dropdown data:", error);
        }
    };

    const fetchMachinesByDepartment = async (departmentId) => {
        // console.log("Fetching machines for department ID:", departmentId); // Log the department ID
        try {
            const response = await axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/machinelist/by-department/${departmentId}`);
            // console.log("Machines fetched from backend:", response.data); // Log the response data
            setFilteredMachines(response.data); // Set the filtered machines
        } catch (error) {
            console.error("Error fetching machines by department:", error);
            setFilteredMachines([]); // Reset if there's an error
        }
    };

    const handleChange = (e, field, idField, optionsArray = [], optionKey = "") => {
        const value = e.target.value;
        if (e.target.value === "other") {
            if (field === "product_name") {
                setShowNewProductForm(true);
            } else if (field === "RM_name") {
                setShowNewRawMaterialForm(true);
            } else if (field === "section_name") {
                setShowNewSectionForm(true);
            } else if (field === "process_name") {
                setShowNewProcessForm(true);
            } else if (field === "machine_name") {
                setShowNewMachineForm(true);
            }
            onExpandDrawer();
            setFormData(prevState => ({
                ...prevState,
                [field]: value,
                [idField]: value // Assuming idField is the same as value for now
            }));
        } else {
            setShowNewProductForm(false);
            setShowNewRawMaterialForm(false);
            setShowNewSectionForm(false);
            setShowNewProcessForm(false);
            setShowNewMachineForm(false);

            setFormData(prevState => ({
                ...prevState,
                [field]: value,
                [idField]: value // Assuming idField is the same as value for now
            }));

            if (optionsArray.length > 0 && optionKey) {
                const selectedItem = optionsArray.find(item => item[optionKey] === value) || {};
                setFormData(prevState => ({
                    ...prevState,
                    [field]: selectedItem[optionKey] || "",
                    [idField]: selectedItem.id || "" // This should be the integer ID
                }));
            } else {
                setFormData(prevState => ({
                    ...prevState,
                    [e.target.name]: e.target.value
                }));
            }

            // If the field is section_name, fetch machines based on the selected section
        if (field === "section_name") {
            const selectedSection = options.sections.find(section => section.dept_name === value);
            if (selectedSection) {
                const departmentId = selectedSection.id; // Get the integer ID
                // console.log("Section changed, fetching machines for department ID:", departmentId);
                fetchMachinesByDepartment(departmentId); // Fetch machines when section changes
                setFormData(prevState => ({
                    ...prevState,
                    section_id: departmentId // Store the integer ID in section_id
                }));
            }
        }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        onSave(formData);
    };

    const handleNewProductSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${process.env.REACT_APP_PJCBACKEND_URL}/api/main-product`, {
                product_name: formData.new_product_name,
                full_name: formData.full_name,
                // unit_cost: formData.unit_cost,
                descrip: formData.descrip
            });
            resetNewProductForm();
            fetchDropdownData();
        } catch (error) {
            console.error("Error adding new product:", error);
            setErrorMessage("Failed to add new product. Please try again.");
        }
    };

    const handleNewRawMaterialSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${process.env.REACT_APP_PJCBACKEND_URL}/api/products`, {
                product_code: formData.new_RM_product_code,
                product_name: formData.new_RM_product_name,
                quantity: formData.new_RM_quantity,
                daily_consumption: formData.new_RM_daily_consumption,
                backup_time: formData.new_RM_backup_time,
                base_uom: formData.new_RM_base_uom
            });
            resetNewRawMaterialForm();
            fetchDropdownData();
        } catch (error) {
            console.error("Error adding new raw material:", error);
            setErrorMessage("Failed to add new raw material. Please try again.");
        }
    };

    const handleNewSectionSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${process.env.REACT_APP_PJCBACKEND_URL}/api/department`, {
                dept_name: formData.new_section_name,
                remark: formData.new_section_remark
            });
            resetNewSectionForm();
            fetchDropdownData();
        } catch (error) {
            console.error("Error adding new section:", error);
            setErrorMessage("Failed to add new section. Please try again.");
        }
    };

    const handleNewProcessSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${process.env.REACT_APP_PJCBACKEND_URL}/api/proccess`, {
                process_name: formData.new_process_name,
                remark: formData.new_process_remark
            });
            resetNewProcessForm();
            fetchDropdownData();
        } catch (error) {
            console.error("Error adding new process:", error);
            setErrorMessage("Failed to add new process. Please try again.");
        }
    };

    const handleNewMachineSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${process.env.REACT_APP_PJCBACKEND_URL}/api/machinelist`, {
                MachineNo: formData.new_machine_no,
                MachineName: formData.new_machine_name,
                startDate: formData.new_machine_start_date,
                endDate: formData.new_machine_end_date
            });
            resetNewMachineForm();
            fetchDropdownData();
        } catch (error) {
            console.error("Error adding new machine:", error);
            setErrorMessage("Failed to add new machine. Please try again.");
        }
    };

    const resetNewProductForm = () => {
        setFormData(prevState => ({
            ...prevState,
            new_product_name: "",
            full_name: "",
            // unit_cost: "",
            descrip: ""
        }));
        setShowNewProductForm(false);
    };

    const resetNewRawMaterialForm = () => {
        setFormData(prevState => ({
            ...prevState,
            new_RM_product_code: "",
            new_RM_product_name: "",
            new_RM_quantity: "",
            new_RM_daily_consumption: "",
            new_RM_backup_time: "",
            new_RM_base_uom: ""
        }));
        setShowNewRawMaterialForm(false);
    };

    const resetNewSectionForm = () => {
        setFormData(prevState => ({
            ...prevState,
            new_section_name: "",
            new_section_remark: ""
        }));
        setShowNewSectionForm(false);
    };

    const resetNewProcessForm = () => {
        setFormData(prevState => ({
            ...prevState,
            new_process_name: "",
            new_process_remark: ""
        }));
        setShowNewProcessForm(false);
    };

    const resetNewMachineForm = () => {
        setFormData(prevState => ({
            ...prevState,
            new_machine_no: "",
            new_machine_name: "",
            new_machine_start_date: "",
            new_machine_end_date: ""
        }));
        setShowNewMachineForm(false);
    };

    return (
        <>
        {/* Header */}
        <Box sx={{ mb: 3, borderBottom: `2px solid ${grey[300]}`, paddingBottom: 1, bgcolor: grey[200], p: 2, textAlign: "center" }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ textAlign: "center", mb: 1, mt: 1 }}>
                        {editData ? "Edit Product Wise Machine" : "Add Product Wise Machine"}
                    </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
            {/* Main Form Box */}
            <Box sx={{ ml: 2, mt: 2, mr: 2, mb: 0, p: 2, border: "1px solid #ccc", borderRadius: 2, flex: 1 }}>
                
                <Box sx={{ display: "flex", gap: 2, marginTop: 1, marginBottom: 1 }}>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <FormControl fullWidth margin="dense" size="small">
                                    <InputLabel>Product</InputLabel>
                                    <Select
                                        value={formData.product_name}
                                        onChange={(e) => handleChange(e, "product_name", "product_id", options.products, "product_name")}
                                    >
                                        <MenuItem 
                                            value="other" 
                                            sx={{ 
                                                color: 'blue', 
                                                // backgroundColor: 'lightgrey', 
                                                '&:hover': {
                                                    backgroundColor: 'blue', // Change to a darker shade of blue or any color you prefer
                                                    color: 'white' // Change text color on hover
                                                }
                                            }} 
                                        >
                                            Add New Product
                                        </MenuItem>
                                        {options.products.map((item) => (
                                            <MenuItem key={item.id} value={item.product_name}>{item.product_name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl fullWidth margin="dense" size="small">
                                    <InputLabel>Raw Material</InputLabel>
                                    <Select
                                        value={formData.RM_name}
                                        onChange={(e) => handleChange(e, "RM_name", "RM_id", options.rawMaterials, "product_name")}
                                    >
                                        <MenuItem 
                                            value="other" 
                                            sx={{ 
                                                color: 'blue', 
                                                // backgroundColor: 'lightgrey', 
                                                '&:hover': {
                                                    backgroundColor: 'blue', // Change to a darker shade of blue or any color you prefer
                                                    color: 'white' // Change text color on hover
                                                }
                                            }} 
                                        >
                                            Add New Raw Material
                                        </MenuItem>
                                        {options.rawMaterials.map((item) => (
                                            <MenuItem key={item.id} value={item.product_name}>{item.product_name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl fullWidth margin="dense" size="small">
                                    <InputLabel>Section</InputLabel>
                                    <Select
                                        value={formData.section_name}
                                        onChange={(e) => handleChange(e, "section_name", "section_id", options.sections, "dept_name")}
                                    >
                                        <MenuItem 
                                            value="other" 
                                            sx={{ 
                                                color: 'blue', 
                                                // backgroundColor: 'lightgrey', 
                                                '&:hover': {
                                                    backgroundColor: 'blue', // Change to a darker shade of blue or any color you prefer
                                                    color: 'white' // Change text color on hover
                                                }
                                            }} 
                                        >
                                            Add New Section
                                        </MenuItem>
                                        {options.sections.map((item) => (
                                            <MenuItem key={item.id} value={item.dept_name}>{item.dept_name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl fullWidth margin="dense" size="small">
                                    <InputLabel>Process</InputLabel>
                                    <Select 
                                        value={formData.process_name} 
                                        onChange={(e) => handleChange(e, "process_name", "process_id", options.processes, "process_name")}
                                    >
                                        <MenuItem 
                                            value="other" 
                                            sx={{ 
                                                color: 'blue', 
                                                // backgroundColor: 'lightgrey', 
                                                '&:hover': {
                                                    backgroundColor: 'blue', // Change to a darker shade of blue or any color you prefer
                                                    color: 'white' // Change text color on hover
                                                }
                                            }} 
                                        >
                                            Add New Process
                                        </MenuItem>
                                        {options.processes.map((item) => (
                                            <MenuItem key={item.id} value={item.process_name}>{item.process_name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl fullWidth margin="dense" size="small">
                                    <InputLabel>Machine</InputLabel>
                                    <Select
                                        value={formData.machine_name}
                                        onChange={(e) => handleChange(e, "machine_name", "machine_id", filteredMachines, "MachineName")}
                                    >
                                        <MenuItem 
                                            value="other" 
                                            sx={{ 
                                                color: 'blue', 
                                                // backgroundColor: 'lightgrey', 
                                                '&:hover': {
                                                    backgroundColor: 'blue', // Change to a darker shade of blue or any color you prefer
                                                    color: 'white' // Change text color on hover
                                                }
                                            }} 
                                        >
                                            Add New Machine
                                        </MenuItem>
                                        {/* {options.machines.map((item) => (
                                            <MenuItem key={item.id} value={item.MachineName}>{item.MachineName}</MenuItem>
                                        ))} */}
                                        {filteredMachines.map((item) => (
                                            <MenuItem key={item.id} value={item.MachineName}>{item.MachineName}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField fullWidth margin="dense" size="small" label="Quantity" type="number" name="quantity" value={formData.quantity} onChange={handleChange} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField fullWidth margin="dense" size="small" label="Electricity (KW/hour)" type="number" name="elect_unit" value={formData.elect_unit} onChange={handleChange} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField fullWidth margin="dense" size="small" label="Electricity Cost" type="number" name="elect_cost" value={formData.elect_cost} onChange={handleChange} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField fullWidth margin="dense" size="small" label="Manpower" type="number" name="manpower" value={formData.manpower} onChange={handleChange} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField fullWidth margin="dense" size="small" label="Manpower Cost" type="number" name="manpower_cost" value={formData.manpower_cost} onChange={handleChange} />
                            </Grid>
                        </Grid>

                        {/* Buttons */}
                        <Box sx={{ mt: 3, borderTop: `2px solid ${grey[300]}`, paddingTop: 3, display: "flex", justifyContent: "space-between" }}>
                            <Button variant="contained" size="small" color="success" sx={{ fontSize: "0.65rem", padding: "3px 6px" }} type="submit">
                                Save
                            </Button>
                            <Button variant="outlined" size="small" color="error" sx={{ fontSize: "0.65rem", padding: "3px 6px" }} onClick={onClose}>
                                Cancel
                            </Button>
                        </Box>
                    </form>
                </Box>
            </Box>

            {/* New Product Form */}
            {showNewProductForm && (
                <Box sx={{ border: "1px solid #ccc", borderRadius: 2, p: 2, mt: 2, mr: 2, flex: 1 }}>
                    <Box sx={{ mb: 3, borderBottom: `2px solid ${grey[300]}`, bgcolor: grey[200], p: 1, textAlign: "center" }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ textAlign: "center"}}>
                            New Product Details
                        </Typography>
                    </Box>
                    <form onSubmit={handleNewProductSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Enter Product"
                                    name="new_product_name"
                                    value={formData.new_product_name}
                                    onChange={(e) => setFormData({ ...formData, new_product_name: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Full Name"
                                    name="full_name"
                                    value={formData.full_name}
                                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                                />
                            </Grid>
                            {/* <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Unit Cost"
                                    name="unit_cost"
                                    value={formData.unit_cost}
                                    onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value })}
                                />
                            </Grid> */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Description"
                                    name="descrip"
                                    value={formData.descrip}
                                    onChange={(e) => setFormData({ ...formData, descrip: e.target.value })}
                                    multiline
                                    rows={3}
                                />
                            </Grid>
                        </Grid>

                        {/* Buttons for the new product form */}
                        <Box sx={{ mt: 3, borderTop: `2px solid ${grey[300]}`, paddingTop: 3, display: "flex", justifyContent: "space-between" }}>
                            <Button variant="contained" size="small" color="success" sx={{ fontSize: "0.65rem", padding: "3px 6px" }} type="submit">
                                Save
                            </Button>
                            <Button variant="outlined" size="small" color="error" sx={{ fontSize: "0.65rem", padding: "3px 6px" }} onClick={() => setShowNewProductForm(false)}>
                                Cancel
                            </Button>
                        </Box>
                    </form>
                </Box>
            )}

            {/* New Raw Material Form */}
            {showNewRawMaterialForm && (
                <Box sx={{ border: "1px solid #ccc", borderRadius: 2, p: 2, mt: 2, mr: 2, flex: 1 }}>
                    <Box sx={{ mb: 3, borderBottom: `2px solid ${grey[300]}`, bgcolor: grey[200], p: 1, textAlign: "center" }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ textAlign: "center"}}>
                            New Raw Material Details
                        </Typography>
                    </Box>
                    <form onSubmit={handleNewRawMaterialSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Product Code"
                                    name="new_RM_product_code"
                                    value={formData.new_RM_product_code}
                                    onChange={(e) => setFormData({ ...formData, new_RM_product_code: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Product Name"
                                    name="new_RM_product_name"
                                    value={formData.new_RM_product_name}
                                    onChange={(e) => setFormData({ ...formData, new_RM_product_name: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Quantity"
                                    name="new_RM_quantity"
                                    value={formData.new_RM_quantity}
                                    onChange={(e) => setFormData({ ...formData, new_RM_quantity: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Daily Consumption"
                                    name="new_RM_daily_consumption"
                                    value={formData.new_RM_daily_consumption}
                                    onChange={(e) => setFormData({ ...formData, new_RM_daily_consumption: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Backup Time"
                                    name="new_RM_backup_time"
                                    value={formData.new_RM_backup_time}
                                    onChange={(e) => setFormData({ ...formData, new_RM_backup_time: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Base UOM"
                                    name="new_RM_base_uom"
                                    value={formData.new_RM_base_uom}
                                    onChange={(e) => setFormData({ ...formData, new_RM_base_uom: e.target.value })}
                                />
                            </Grid>
                        </Grid>

                        {/* Buttons for the new raw material form */}
                        <Box sx={{ mt: 3, borderTop: `2px solid ${grey[300]}`, paddingTop: 3, display: "flex", justifyContent: "space-between" }}>
                            <Button variant="contained" size="small" color="success" sx={{ fontSize: "0.65rem", padding: "3px 6px" }} type="submit">
                                Save
                            </Button>
                            <Button variant="outlined" size="small" color="error" sx={{ fontSize: "0.65rem", padding: "3px 6px" }} onClick={() => setShowNewRawMaterialForm(false)}>
                                Cancel
                            </Button>
                        </Box>
                    </form>
                </Box>
            )}

            {/* New Section Form */}
            {showNewSectionForm && (
                <Box sx={{ border: "1px solid #ccc", borderRadius: 2, p: 2, mt: 2, mr: 2, flex: 1 }}>
                    <Box sx={{ mb: 3, borderBottom: `2px solid ${grey[300]}`, bgcolor: grey[200], p: 1, textAlign: "center" }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ textAlign: "center"}}>
                            New Section Details
                        </Typography>
                    </Box>
                    <form onSubmit={handleNewSectionSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Department Name"
                                    name="new_section_name"
                                    value={formData.new_section_name}
                                    onChange={(e) => setFormData({ ...formData, new_section_name: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Remark"
                                    name="new_section_remark"
                                    value={formData.new_section_remark}
                                    onChange={(e) => setFormData({ ...formData, new_section_remark: e.target.value })}
                                />
                            </Grid>
                        </Grid>

                        {/* Buttons for the new section form */}
                        <Box sx={{ mt: 3, borderTop: `2px solid ${grey[300]}`, paddingTop: 3, display: "flex", justifyContent: "space-between" }}>
                            <Button variant="contained" size="small" color="success" sx={{ fontSize: "0.65rem", padding: "3px 6px" }} type="submit">
                                Save
                            </Button>
                            <Button variant="outlined" size="small" color="error" sx={{ fontSize: "0.65rem", padding: "3px 6px" }} onClick={() => setShowNewSectionForm(false)}>
                                Cancel
                            </Button>
                        </Box>
                    </form>
                </Box>
            )}

            {/* New Process Form */}
            {showNewProcessForm && (
                <Box sx={{ border: "1px solid #ccc", borderRadius: 2, p: 2, mt: 2, mr: 2, flex: 1 }}>
                    <Box sx={{ mb: 3, borderBottom: `2px solid ${grey[300]}`, bgcolor: grey[200], p: 1, textAlign: "center" }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ textAlign: "center"}}>
                            New Process Details
                        </Typography>
                    </Box>
                    <form onSubmit={handleNewProcessSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Process Name"
                                    name="new_process_name"
                                    value={formData.new_process_name}
                                    onChange={(e) => setFormData({ ...formData, new_process_name: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Remark"
                                    name="new_process_remark"
                                    value={formData.new_process_remark}
                                    onChange={(e) => setFormData({ ...formData, new_process_remark: e.target.value })}
                                />
                            </Grid>
                        </Grid>

                        {/* Buttons for the new process form */}
                        <Box sx={{ mt: 3, borderTop: `2px solid ${grey[300]}`, paddingTop: 3, display: "flex", justifyContent : "space-between" }}>
                            <Button variant="contained" size="small" color="success" sx={{ fontSize: "0.65rem", padding: "3px 6px" }} type="submit">
                                Save
                            </Button>
                            <Button variant="outlined" size="small" color="error" sx={{ fontSize: "0.65rem", padding: "3px 6px" }} onClick={() => setShowNewProcessForm(false)}>
                                Cancel
                            </Button>
                        </Box>
                    </form>
                </Box>
            )}

            {/* New Machine Form */}
            {showNewMachineForm && (
                <Box sx={{ border: "1px solid #ccc", borderRadius: 2, p: 2, mt: 2, mr: 2, flex: 1 }}>
                    <Box sx={{ mb: 3, borderBottom: `2px solid ${grey[300]}`, bgcolor: grey[200], p: 1, textAlign: "center" }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ textAlign: "center"}}>
                            New Machine Details
                        </Typography>
                    </Box>
                    <form onSubmit={handleNewMachineSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Machine No"
                                    name="new_machine_no"
                                    value={formData.new_machine_no}
                                    onChange={(e) => setFormData({ ...formData, new_machine_no: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Machine Name"
                                    name="new_machine_name"
                                    value={formData.new_machine_name}
                                    onChange={(e) => setFormData({ ...formData, new_machine_name: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="Start Date"
                                    name="new_machine_start_date"
                                    type="date"
                                    value={formData.new_machine_start_date}
                                    onChange={(e) => setFormData({ ...formData, new_machine_start_date: e.target.value })}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    size="small"
                                    label="End Date"
                                    name="new_machine_end_date"
                                    type="date"
                                    value={formData.new_machine_end_date}
                                    onChange={(e) => setFormData({ ...formData, new_machine_end_date: e.target.value })}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>

                        {/* Buttons for the new machine form */}
                        <Box sx={{ mt: 3, borderTop: `2px solid ${grey[300]}`, paddingTop: 3, display: "flex", justifyContent: "space-between" }}>
                            <Button variant="contained" size="small" color="success" sx={{ fontSize: "0.65rem", padding: "3px 6px" }} type="submit">
                                Save
                            </Button>
                            <Button variant="outlined" size="small" color="error" sx={{ fontSize: "0.65rem", padding: "3px 6px" }} onClick={() => setShowNewMachineForm(false)}>
                                Cancel
                            </Button>
                        </Box>
                    </form>
                </Box>
            )}
        </Box>
        </>
    );
};

export default AddEditProductWiseMachine;